import * as React from 'react';
import styled from 'styled-components';
import { IRatioReportSettings } from '../../../../data/providers/settings/reports/ratioReportSettingsProvider';
import {
  ISelectableMetric,
  ISelectableTag,
  ISelectableTestType,
} from '../../../../data/types';
import { CardHeader, CardHeaderLeft } from '../../../shared/card/CardHeader';
import { ControlLabel } from '../../../shared/form/ControlLabel';
import { ControlSpacer } from '../../../shared/form/ControlSpacer';
import { SingleMetricSelect } from '../../../shared/form/selects/SingleMetricSelect';
import { TagSelect } from '../../../shared/form/selects/TagsSelect';
import { TestTypeSelect } from '../../../shared/form/selects/TestTypeSelect';
import { Step } from '../../../shared/form/Step';
import { FormSection } from '../../utils/settings/FormSection';

interface IProps {
  settings: IRatioReportSettings;
  onSettingsChange: (settings: IRatioReportSettings) => void;
}

export const Form = (props: IProps): React.ReactElement => {
  /**
   * @description handles change to metric one test types
   * @param sTags
   * @returns
   */
  const metricOneTestTypeChangeHandler = (
    testType: ISelectableTestType | ISelectableTestType[]
  ): void => {
    props.onSettingsChange({
      ...props?.settings,
      metricOne: {
        ...props?.settings?.metricOne,
        testType: testType as ISelectableTestType,
      },
    });
  };

  /**
   * @description handles change to metric two test types
   * @param sTags
   * @returns
   */
  const metricTwoTestTypeChangeHandler = (
    testType: ISelectableTestType | ISelectableTestType[]
  ): void => {
    props.onSettingsChange({
      ...props?.settings,
      metricTwo: {
        ...props?.settings?.metricTwo,
        testType: testType as ISelectableTestType,
      },
    });
  };

  /**
   * @description handles change to metric one test types
   * @param sTags
   * @returns
   */
  const metricOneChangeHandler = (metric: ISelectableMetric): void => {
    const metricTwoSettings =
      metric.entity.units === props?.settings?.metricTwo?.metric.entity.units
        ? props?.settings?.metricTwo
        : { ...props?.settings?.metricTwo, metric: null };
    props.onSettingsChange({
      ...props?.settings,
      metricOne: {
        ...props?.settings?.metricOne,
        metric,
      },
      metricTwo: metricTwoSettings,
    });
  };

  /**
   * @description handles change to metric two test types
   * @param sTags
   * @returns
   */
  const metricTwoChangeHandler = (metric: ISelectableMetric): void => {
    const update = {
      ...props?.settings,
      metricTwo: {
        ...props?.settings?.metricTwo,
        metric,
      },
    };
    props.onSettingsChange(update);
  };

  /**
   * @description handles change to metric one tags
   * @param sTags
   * @returns
   */
  const metricOneTagsChangeHandler = (
    metricOneTags: ISelectableTag[]
  ): void => {
    const update = {
      ...props?.settings,
      metricOne: {
        ...props?.settings?.metricOne,
        tags: metricOneTags,
      },
    };
    props.onSettingsChange(update);
  };

  /**
   * @description handles change to metric two tags
   * @param sTags
   * @returns
   */
  const metricTwoTagsChangeHandler = (
    metricTwoTags: ISelectableTag[]
  ): void => {
    props.onSettingsChange({
      ...props?.settings,
      metricTwo: {
        ...props?.settings?.metricTwo,
        tags: metricTwoTags,
      },
    });
  };

  return (
    <div style={{ padding: '10px', paddingTop: '20px' }}>
      <FormSection>
        <CardHeader>
          <CardHeaderLeft>
            <Step>2</Step>
            <h6>
              Select 1<sup>st</sup> Metric
            </h6>
          </CardHeaderLeft>
        </CardHeader>
        <div style={{ padding: '10px' }}>
          <ControlSpacer>
            <ControlLabel>Test Type</ControlLabel>
            <TestTypeSelect
              value={props?.settings?.metricOne?.testType}
              onChange={metricOneTestTypeChangeHandler}
              disabled={false}
              allowMultiple={false}
            />
          </ControlSpacer>
          <ControlSpacer>
            <ControlLabel>Tags</ControlLabel>
            <TagSelect
              value={props?.settings?.metricOne?.tags}
              onChange={metricOneTagsChangeHandler}
              disabled={!props?.settings?.metricOne?.testType}
            />
          </ControlSpacer>
          <ControlSpacer>
            <ControlLabel>Metric</ControlLabel>
            <SingleMetricSelect
              excludeAsymmetry
              testTypeId={props?.settings?.metricOne?.testType?.value}
              disabled={!props?.settings?.metricOne?.testType}
              value={props?.settings?.metricOne?.metric}
              onChange={metricOneChangeHandler}
            />
          </ControlSpacer>
        </div>
      </FormSection>
      <FormSection>
        <CardHeader>
          <CardHeaderLeft>
            <Step>3</Step>
            <h6>
              Select 2<sup>nd</sup> Metric
            </h6>
          </CardHeaderLeft>
        </CardHeader>
        <div style={{ padding: '10px' }}>
          <ControlSpacer>
            <ControlLabel>Test Type</ControlLabel>
            <TestTypeSelect
              value={props?.settings?.metricTwo?.testType}
              onChange={metricTwoTestTypeChangeHandler}
              disabled={false}
              allowMultiple={false}
            />
          </ControlSpacer>
          <ControlSpacer>
            <ControlLabel>Tags</ControlLabel>
            <TagSelect
              value={props?.settings?.metricTwo?.tags}
              onChange={metricTwoTagsChangeHandler}
              disabled={!props?.settings?.metricTwo?.testType}
            />
          </ControlSpacer>
          <ControlSpacer>
            <ControlLabel>Metric</ControlLabel>
            <SingleMetricSelect
              excludeAsymmetry
              units={props?.settings?.metricOne?.metric?.entity?.units}
              testTypeId={props?.settings?.metricTwo?.testType?.value}
              disabled={!props?.settings?.metricTwo?.testType}
              value={props?.settings?.metricTwo?.metric}
              onChange={metricTwoChangeHandler}
            />
          </ControlSpacer>
        </div>
      </FormSection>
    </div>
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const LeftSide = styled(ControlSpacer as any)`
  flex: 1;
  margin-right: 10px;
  width: 100%;
  @media (max-width: 990px) {
    margin-right: 0;
  }
`;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const RightSide = styled(ControlSpacer as any)`
  flex: 1;
  width: 100%;
`;
